import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { NoveltyCheckTaskStep } from '@/domains/novelty-check-report/enums';
import type { NoveltyCheckReportContext } from '../novelty-check-report-context';
import type { NoveltyCheckReportState } from '../novelty-check-report-state';
import { Logger } from '@patsnap/tahiti-core';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { RetrievalElementsService, NoveltyCheckReportTitleService, TechFeaturesService } from '../../retrieval-result-report-service';
import { NoveltyCheckTaskStatusModel } from '@/domains/novelty-check-report/models/novelty-check-task-status-model';
import { NoveltyCheckTaskStatusService } from '../../task-status-service';
import { SummaryTechFeaturesService } from '../../summary-tech-features-service';

/**
 * 检索要素识别开始状态
 * 用于处理查新报告中的检索要素识别过程的开始状态
 */
@injectable()
export class RetrievalElementIdentifyStartState implements NoveltyCheckReportState {
    constructor(
        @inject(RetrievalElementsService)
        private readonly _retrievalElementsService: RetrievalElementsService,
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(SummaryTechFeaturesService)
        private readonly _summaryTechFeatureService: SummaryTechFeaturesService,
        @inject(NoveltyCheckTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: NoveltyCheckTaskStatusModel,
        @inject(NoveltyCheckTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: NoveltyCheckTaskStatusService,
        @inject(NoveltyCheckReportTitleService)
        private readonly _noveltyCheckReportTitleService: NoveltyCheckReportTitleService,
        @inject(TechFeaturesService)
        private readonly _techFeaturesService: TechFeaturesService,
    ) {}

    /**
     * 获取当前状态对应的任务步骤
     */
    getStep(): NoveltyCheckTaskStep {
        return NoveltyCheckTaskStep.NOVELTY_SEARCH_RETRIEVAL_ELEMENTS;
    }

    /**
     * 进入状态时的处理逻辑
     * @param _context 查新报告状态上下文
     */
    async enter(): Promise<void> {
        await this._noveltyCheckTaskStatusService.pullTaskStatus();
        this._logger.info('进入检索要素识别开始状态');
        this._switchTaskStep(NoveltyCheckTaskStep.NOVELTY_SEARCH_RETRIEVAL_ELEMENTS);
        // 获取检索要素数据
        await this._retrievalElementsService.pullRetrievalElements();
        await Promise.all([
            // this._summaryTechFeatureService.pullSummary(),
            // 获取新颖性报告名称
            // this._noveltyCheckReportTitleService.pullNoveltyCheckReportTitle(),
            // 获取技术特征
            // this._techFeaturesService.pullTechFeatures(),
        ]);
    }

    /**
     * 切换任务步骤
     * @param step 目标步骤
     */
    private _switchTaskStep(step: NoveltyCheckTaskStep) {
        this._noveltyCheckTaskStatusModel.update(state => {
            state.taskStep = step;
            return state;
        });
    }
    /**
     * 处理下一步操作
     * @param context 查新报告状态上下文
     */
    async handleNext(context: NoveltyCheckReportContext): Promise<void> {
        // 检索要素识别完成后，进入下一个状态（通常是搜索完成状态）
        const nextState = context.getState(NoveltyCheckTaskStep.SEARCH_FINISHED);
        context.transitionTo(nextState);
    }

    /**
     * 处理上一步操作
     * @param context 查新报告状态上下文
     */
    async handlePrevious(context: NoveltyCheckReportContext): Promise<void> {
        // 返回到技术特征识别状态
        const previousState = context.getState(NoveltyCheckTaskStep.NOVELTY_SEARCH_TECH_FEATURE);
        await context.transitionTo(previousState);
    }
}
